'use client'

import { useEffect, useState } from 'react'

interface ArticleProgressBarProps {
  /**
   * Article element ref or selector
   */
  articleSelector?: string
  articleElement?: HTMLElement | null
}

/**
 * ArticleProgressBar - Shows reading progress below article title
 * Thin progress bar that fills from 0% to 100% based on reading progress
 */
export default function ArticleProgressBar({ 
  articleSelector,
  articleElement 
}: ArticleProgressBarProps) {
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      return
    }

    let rafId: number | null = null
    let articleEl: HTMLElement | null = null

    const updateProgress = () => {
      if (articleElement) {
        articleEl = articleElement
      } else if (articleSelector) {
        articleEl = document.querySelector(articleSelector) as HTMLElement
      }

      if (!articleEl) {
        setProgress(0)
        return
      }

      // Calculate progress for specific article element
      const rect = articleEl.getBoundingClientRect()
      const articleTop = rect.top + window.scrollY
      const articleHeight = rect.height
      const viewportHeight = window.innerHeight
      const scrollTop = window.scrollY

      const articleBottom = articleTop + articleHeight
      const viewportTop = scrollTop
      const viewportBottom = scrollTop + viewportHeight

      let progressPercent = 0

      if (viewportTop >= articleBottom) {
        progressPercent = 100
      } else if (viewportBottom <= articleTop) {
        progressPercent = 0
      } else {
        const scrolled = viewportTop - articleTop
        const total = articleHeight - viewportHeight
        progressPercent = total > 0 ? (scrolled / total) * 100 : 0
        progressPercent = Math.min(100, Math.max(0, progressPercent))
      }

      setProgress(progressPercent)
      rafId = requestAnimationFrame(updateProgress)
    }

    rafId = requestAnimationFrame(updateProgress)

    const handleScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(updateProgress)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll, { passive: true })
    updateProgress()

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [articleSelector, articleElement])

  return (
    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700" aria-hidden="true">
      <div
        className="h-full bg-gradient-to-r from-red-600 to-red-500 transition-all duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  )
}

